/**
 * Seasons Service - Phase 4 Architecture
 * Handles all season-related operations including lifecycle and leaderboards
 */
import { typedSupabase } from '../lib/typed-supabase';
import type { IService, ServiceResponse } from './service-registry';
import type { Season, SeasonWithTheme, UserSeasonProgress, SeasonLeaderboardEntry, SeasonHistory, UserSeasonStats, CreateSeasonParams, CreateSeasonResult, ActivateSeasonResult, EndSeasonResult, SeasonTimeRemaining, } from '@flowstate/types';
// ============================================================================
// SEASONS SERVICE - Phase 4 Architecture
// ============================================================================
export interface ISeasonsService extends IService {
    getActiveSeason(): Promise<ServiceResponse<SeasonWithTheme | null>>;
    getUserSeasonStats(userId: string): Promise<ServiceResponse<UserSeasonStats | null>>;
    getUserSeasonProgress(userId: string, seasonId: string): Promise<ServiceResponse<UserSeasonProgress | null>>;
    getSeasonLeaderboard(seasonId: string, limit?: number): Promise<ServiceResponse<SeasonLeaderboardEntry[]>>;
    getSeasonTimeRemaining(seasonId: string): Promise<ServiceResponse<SeasonTimeRemaining | null>>;
    getSeasonHistory(userId?: string): Promise<ServiceResponse<SeasonHistory[]>>;
    getAllSeasons(): Promise<ServiceResponse<Season[]>>;
    createSeason(params: CreateSeasonParams): Promise<ServiceResponse<CreateSeasonResult>>;
    activateSeason(seasonId: string): Promise<ServiceResponse<ActivateSeasonResult>>;
    endSeason(seasonId: string): Promise<ServiceResponse<EndSeasonResult>>;
    getSeasonById(seasonId: string): Promise<ServiceResponse<Season | null>>;
}
export class SeasonsService implements ISeasonsService {
    readonly name = "SeasonsService";
    async initialize(): Promise<void> {
        // no-op
    }
    async dispose(): Promise<void> {
        // no-op
    }
    /**
     * Get the currently active season
     */
    async getActiveSeason(): Promise<ServiceResponse<SeasonWithTheme | null>> {
        const startTime = Date.now();
        try {
            // Try RPC first, fall back to direct query if RPC doesn't exist
            try {
                const { data, error } = await typedSupabase.rpc('get_active_season');
                if (!error && data) {
                    const season = data?.[0] || null;
                    return { success: true, data: season };
                }
            }
            catch (rpcError) {
                // RPC not available; fall through to direct query below
            }
            const { data, error } = await typedSupabase
                .from('gamification_seasons')
                .select(`
          *,
          gamification_themes (
            name,
            config
          )
        `)
                .eq('is_active', true)
                .eq('is_completed', false)
                .gte('start_date', new Date().toISOString())
                .lte('end_date', new Date().toISOString())
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();
            if (error) {
                return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name, context: { error } };
            }
            if (data) {
                const season: SeasonWithTheme = {
                    ...data,
                    theme_name: data.gamification_themes?.name || 'Default',
                    theme_config: data.gamification_themes?.config || {}
                };
                return { success: true, data: season };
            }
            return { success: true, data: null };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name, context: { error } };
        }
    }
    /**
     * Get user's season stats (both main and season XP)
     * Due to complexity, returns null on error for backward compatibility
     */
    async getUserSeasonStats(userId: string): Promise<ServiceResponse<UserSeasonStats | null>> {
        const startTime = Date.now();
        try {
            // Try RPC first
            try {
                const { data, error } = await typedSupabase.rpc('get_user_season_stats', {
                    p_user_id: userId,
                });
                if (!error && data) {
                    return { success: true, data: data };
                }
            }
            catch (rpcError) {
                // RPC not available; fall through to direct queries below
            }
            // Fallback: Direct queries (complex logic preserved for backward compat)
            const now = new Date();
            // Get main stats
            const { data: mainData, error: mainError } = await typedSupabase
                .from('profiles')
                .select('xp, xp_this_week, xp_this_month, xp_today, current_streak, longest_streak')
                .eq('id', userId)
                .single();
            if (mainError) {
                return { success: false, error: mainError.message, code: "FETCH_FAILED", service: this.name, context: { userId, error: mainError } };
            }
            // Get active season
            const { data: seasonData, error: seasonError } = await typedSupabase
                .from('gamification_seasons')
                .select('id')
                .eq('is_active', true)
                .eq('is_completed', false)
                .gte('start_date', now.toISOString())
                .lte('end_date', now.toISOString())
                .maybeSingle();
            let seasonStats: UserSeasonStats['season'] = { season_id: null };
            if (!seasonError && seasonData) {
                const { data: progressData } = await typedSupabase
                    .from('user_season_progress')
                    .select('*')
                    .eq('user_id', userId)
                    .eq('season_id', seasonData.id)
                    .maybeSingle();
                if (progressData) {
                    // Calculate rank
                    const { data: rankData } = await typedSupabase
                        .from('user_season_progress')
                        .select('season_xp')
                        .eq('season_id', seasonData.id)
                        .order('season_xp', { ascending: false });
                    let currentRank = null;
                    if (rankData) {
                        currentRank = rankData.findIndex((r: {
                            season_xp: number;
                        }) => r.season_xp <= progressData.season_xp) + 1;
                    }
                    seasonStats = {
                        season_id: seasonData.id,
                        season_xp: progressData.season_xp || 0,
                        season_level: progressData.season_level || 1,
                        tasks_completed: progressData.tasks_completed || 0,
                        current_streak: progressData.current_streak || 0,
                        longest_streak: progressData.longest_streak || 0,
                        current_rank: currentRank ?? undefined,
                        best_rank: progressData.best_rank ?? undefined,
                        badges_earned: progressData.badges_earned || [],
                        first_activity_at: progressData.first_activity_at ?? undefined,
                        last_activity_at: progressData.last_activity_at ?? undefined
                    };
                }
            }
            const stats: UserSeasonStats = {
                main: {
                    total_xp: mainData.xp || 0,
                    xp_this_week: mainData.xp_this_week || 0,
                    xp_this_month: mainData.xp_this_month || 0,
                    xp_today: mainData.xp_today || 0,
                    current_streak: mainData.current_streak || 0,
                    longest_streak: mainData.longest_streak || 0,
                },
                season: seasonStats
            };
            return { success: true, data: stats };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name, context: { userId, error } };
        }
    }
    /**
     * Get user's progress for a specific season
     */
    async getUserSeasonProgress(userId: string, seasonId: string): Promise<ServiceResponse<UserSeasonProgress | null>> {
        const startTime = Date.now();
        try {
            const { data, error } = await typedSupabase
                .from('user_season_progress')
                .select('*')
                .eq('user_id', userId)
                .eq('season_id', seasonId)
                .maybeSingle();
            if (error) {
                return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name, context: { userId, seasonId, error } };
            }
            return { success: true, data: data as UserSeasonProgress | null };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name, context: { userId, seasonId, error } };
        }
    }
    /**
     * Get season leaderboard
     */
    async getSeasonLeaderboard(seasonId: string, limit: number = 100): Promise<ServiceResponse<SeasonLeaderboardEntry[]>> {
        const startTime = Date.now();
        try {
            // Try RPC first
            try {
                const { data, error } = await typedSupabase.rpc('get_season_leaderboard', {
                    p_season_id: seasonId,
                    p_limit: limit,
                });
                if (!error && data) {
                    return { success: true, data: data };
                }
            }
            catch (rpcError) {
                // RPC not available; fall through to direct query below
            }
            // Fallback: Direct query
            const { data, error } = await typedSupabase
                .from('user_season_progress')
                .select(`
          user_id,
          season_xp,
          season_level,
          tasks_completed,
          current_streak,
          profiles (
            username,
            full_name,
            avatar_url
          )
        `)
                .eq('season_id', seasonId)
                .order('season_xp', { ascending: false })
                .limit(limit);
            if (error) {
                return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name, context: { seasonId, limit, error } };
            }
            interface LeaderboardQueryResult {
                user_id: string;
                season_xp: number | null;
                season_level: number | null;
                tasks_completed: number | null;
                current_streak: number | null;
                season_xp_this_week?: number | null;
                rank_change?: number | null;
                badges_earned?: string[] | null;
                profiles: {
                    username: string | null;
                    full_name: string | null;
                    avatar_url: string | null;
                } | null;
            }
            const entries: SeasonLeaderboardEntry[] = (data as unknown as LeaderboardQueryResult[] || []).map((entry, index) => ({
                rank: index + 1,
                user_id: entry.user_id,
                username: entry.profiles?.username || 'Unknown',
                full_name: entry.profiles?.full_name ?? '',
                avatar_url: entry.profiles?.avatar_url ?? null,
                season_xp: entry.season_xp || 0,
                season_level: entry.season_level || 1,
                tasks_completed: entry.tasks_completed || 0,
                current_streak: entry.current_streak || 0,
                season_xp_this_week: entry.season_xp_this_week || 0,
                rank_change: entry.rank_change || 0,
                badges_earned: entry.badges_earned || [],
            }));
            return { success: true, data: entries };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name, context: { seasonId, limit, error } };
        }
    }
    /**
     * Get time remaining in a season
     */
    async getSeasonTimeRemaining(seasonId: string): Promise<ServiceResponse<SeasonTimeRemaining | null>> {
        const startTime = Date.now();
        try {
            const { data, error } = await typedSupabase
                .from('gamification_seasons')
                .select('end_date')
                .eq('id', seasonId)
                .single();
            if (error) {
                return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name, context: { seasonId, error } };
            }
            const endDate = new Date(data.end_date);
            const now = new Date();
            const diffMs = endDate.getTime() - now.getTime();
            if (diffMs <= 0) {
                return { success: true, data: null };
            }
            const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
            const total_seconds = Math.floor(diffMs / 1000);
            return { success: true, data: {
                    days,
                    hours,
                    minutes,
                    seconds,
                    total_seconds
                } };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name, context: { seasonId, error } };
        }
    }
    /**
     * Get season history
     */
    async getSeasonHistory(userId?: string): Promise<ServiceResponse<SeasonHistory[]>> {
        const startTime = Date.now();
        try {
            const { data, error } = await typedSupabase
                .from('gamification_seasons')
                .select('*')
                .eq('is_completed', true)
                .order('end_date', { ascending: false });
            if (error) {
                return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name, context: { userId, error } };
            }
            return { success: true, data: (data || []) as SeasonHistory[] };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name, context: { userId, error } };
        }
    }
    /**
     * Get all seasons
     */
    async getAllSeasons(): Promise<ServiceResponse<Season[]>> {
        const startTime = Date.now();
        try {
            const { data, error } = await typedSupabase
                .from('gamification_seasons')
                .select('*')
                .order('created_at', { ascending: false });
            if (error) {
                return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name, context: { error } };
            }
            return { success: true, data: (data || []) as Season[] };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name, context: { error } };
        }
    }
    /**
     * Create a new season
     */
    async createSeason(params: CreateSeasonParams): Promise<ServiceResponse<CreateSeasonResult>> {
        const startTime = Date.now();
        try {
            const { data, error } = await typedSupabase.rpc('create_season', params as unknown as Record<string, unknown>);
            if (error) {
                return { success: false, error: error.message, code: "CREATE_FAILED", service: this.name, context: { params, error } };
            }
            return { success: true, data: data as CreateSeasonResult };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "CREATE_FAILED", service: this.name, context: { params, error } };
        }
    }
    /**
     * Activate a season
     */
    async activateSeason(seasonId: string): Promise<ServiceResponse<ActivateSeasonResult>> {
        const startTime = Date.now();
        try {
            const { data, error } = await typedSupabase.rpc('activate_season', {
                p_season_id: seasonId,
            });
            if (error) {
                return { success: false, error: error.message, code: "ACTIVATE_FAILED", service: this.name, context: { seasonId, error } };
            }
            return { success: true, data: data as ActivateSeasonResult };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "ACTIVATE_FAILED", service: this.name, context: { seasonId, error } };
        }
    }
    /**
     * End a season
     */
    async endSeason(seasonId: string): Promise<ServiceResponse<EndSeasonResult>> {
        const startTime = Date.now();
        try {
            const { data, error } = await typedSupabase.rpc('end_season', {
                p_season_id: seasonId,
            });
            if (error) {
                return { success: false, error: error.message, code: "END_FAILED", service: this.name, context: { seasonId, error } };
            }
            return { success: true, data: data as EndSeasonResult };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "END_FAILED", service: this.name, context: { seasonId, error } };
        }
    }
    /**
     * Get season by ID
     */
    async getSeasonById(seasonId: string): Promise<ServiceResponse<Season | null>> {
        const startTime = Date.now();
        try {
            const { data, error } = await typedSupabase
                .from('gamification_seasons')
                .select('*')
                .eq('id', seasonId)
                .single();
            if (error) {
                return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name, context: { seasonId, error } };
            }
            return { success: true, data: data as Season };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name, context: { seasonId, error } };
        }
    }
    /**
     * Helper: Calculate time remaining from a date string
     * @deprecated Use getSeasonTimeRemaining() instead
     */
    calculateTimeRemaining(endDate: string): {
        days: number;
        hours: number;
        minutes: number;
        seconds: number;
        total_seconds: number;
    } {
        const end = new Date(endDate);
        const now = new Date();
        const diffMs = end.getTime() - now.getTime();
        if (diffMs <= 0) {
            return { days: 0, hours: 0, minutes: 0, seconds: 0, total_seconds: 0 };
        }
        const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
        const total_seconds = Math.floor(diffMs / 1000);
        return { days, hours, minutes, seconds, total_seconds };
    }
    /**
     * Helper: Check if season is currently active
     * @deprecated Check activeSeason directly instead
     */
    isSeasonActive(season: Pick<Season, 'start_date' | 'end_date' | 'is_active' | 'is_completed'> | null | undefined): boolean {
        if (!season)
            return false;
        const now = new Date();
        const start = new Date(season.start_date);
        const end = new Date(season.end_date);
        return now >= start && now <= end && season.is_active && !season.is_completed;
    }
}
// ============================================================================
// SINGLETON EXPORT - Following architectural consolidation plan
// ============================================================================
export const seasonsService = new SeasonsService();
